import { useEffect, useState } from 'react';
import axios from 'axios';

const EMPTY_FORM = {
  company: '',         // site name → contacts.company
  site_number: '',
  address1: '',
  city: '',
  province_state: '',
  postal_zip: '',
  first_name: '',
  last_name: '',
  phone_main: '',
  email_local: ''      // combined with chain's email_domain on submit
};

export default function ChainEntryForm({ recordType, onSubmit, onCancel, submitLabel = 'Save' }) {
  const [chains, setChains] = useState([]);
  const [loadingChains, setLoadingChains] = useState(true);
  const [chainsError, setChainsError] = useState(null);
  const [selectedChain, setSelectedChain] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/contacts/chains')
      .then((res) => {
        if (cancelled) return;
        setChains(res.data.chains || []);
      })
      .catch((err) => {
        if (!cancelled) setChainsError(err.response?.data?.error || err.message);
      })
      .finally(() => { if (!cancelled) setLoadingChains(false); });
    return () => { cancelled = true; };
  }, []);

  function update(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  function pickChain(companyName) {
    if (!companyName) {
      setSelectedChain(null);
      return;
    }
    const chain = chains.find((c) => c.company === companyName) || null;
    setSelectedChain(chain);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!selectedChain) {
      setError('Pick a chain first.');
      return;
    }
    if (!form.company.trim()) {
      setError('Site name is required.');
      return;
    }
    if (!form.last_name.trim()) {
      setError('Contact last name is required.');
      return;
    }

    // Combine email local part with the chain's domain when present
    const domain = (selectedChain.email_domain || '').replace(/^@/, '');
    const email1 = form.email_local.trim() && domain
      ? `${form.email_local.trim()}@${domain}`
      : (form.email_local.trim() || '');

    const payload = {
      // Site/contact (user-entered)
      company:        form.company.trim(),
      first_name:     form.first_name.trim(),
      last_name:      form.last_name.trim(),
      address1:       form.address1.trim(),
      city:           form.city.trim(),
      province_state: form.province_state.trim(),
      postal_zip:     form.postal_zip.trim(),
      phone_main:     form.phone_main.trim(),
      email1,
      country:        'Canada',
      // Inherited from chain
      fuel_brand:      selectedChain.fuel_brand || '',
      account_manager: selectedChain.account_manager || '',
      pos_type:        selectedChain.pos_type || '',
      site_number:     form.site_number.trim()
    };

    setSaving(true);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  const noun = recordType === 'customer' ? 'Customer' : 'Lead';
  const domain = selectedChain?.email_domain || '';

  return (
    <form className="customer-form" onSubmit={handleSubmit} noValidate>
      {error && <p className="error">{error}</p>}
      {chainsError && <p className="error">Failed to load chains: {chainsError}</p>}

      <fieldset>
        <legend>Existing Chain</legend>
        <label className="form-field form-field-wide">
          <span>Select a company already in the database</span>
          <select
            value={selectedChain ? selectedChain.company : ''}
            onChange={(e) => pickChain(e.target.value)}
            disabled={loadingChains || chains.length === 0}
          >
            <option value="">
              {loadingChains
                ? 'Loading chains…'
                : chains.length === 0
                  ? 'No existing chains in the database yet'
                  : '— Select a chain —'}
            </option>
            {chains.map((c) => (
              <option key={c.company} value={c.company}>
                {c.company} · {c.record_count} record{c.record_count === 1 ? '' : 's'}
                {c.fuel_brand ? ` · ${c.fuel_brand}` : ''}
                {c.pos_type ? ` · ${c.pos_type}` : ''}
              </option>
            ))}
          </select>
        </label>

        {selectedChain && (
          <div className="chain-inherit">
            <div className="chain-inherit-head">
              <span className="chain-inherit-title">
                Inheriting from <strong>{selectedChain.company}</strong>
              </span>
              <span className="chain-inherit-hint">
                These values apply to the new {noun.toLowerCase()} automatically
              </span>
            </div>
            <dl className="chain-inherit-grid">
              <div>
                <dt>POS Type</dt>
                <dd>{selectedChain.pos_type || <span className="cell-muted">—</span>}</dd>
              </div>
              <div>
                <dt>Fuel Brand</dt>
                <dd>{selectedChain.fuel_brand || <span className="cell-muted">—</span>}</dd>
              </div>
              <div>
                <dt>Account Manager</dt>
                <dd>{selectedChain.account_manager || <span className="cell-muted">—</span>}</dd>
              </div>
              <div>
                <dt>Email Domain</dt>
                <dd className="cell-mono">
                  {domain ? `@${domain}` : <span className="cell-muted">—</span>}
                </dd>
              </div>
            </dl>
          </div>
        )}
      </fieldset>

      {selectedChain && (
        <>
          <fieldset>
            <legend>Site</legend>
            <div className="form-grid">
              <Field label="Site Name" required wide>
                <input value={form.company} onChange={update('company')} placeholder={`e.g. ${selectedChain.company} – Downtown`} />
              </Field>
              <Field label="Site Number">
                <input value={form.site_number} onChange={update('site_number')} />
              </Field>
              <Field label="Address" wide>
                <input value={form.address1} onChange={update('address1')} />
              </Field>
              <Field label="City">
                <input value={form.city} onChange={update('city')} />
              </Field>
              <Field label="Province / State">
                <input value={form.province_state} onChange={update('province_state')} />
              </Field>
              <Field label="Postal / Zip">
                <input value={form.postal_zip} onChange={update('postal_zip')} />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Site Contact</legend>
            <div className="form-grid">
              <Field label="First Name">
                <input value={form.first_name} onChange={update('first_name')} />
              </Field>
              <Field label="Last Name" required>
                <input value={form.last_name} onChange={update('last_name')} />
              </Field>
              <Field label="Phone Main">
                <input value={form.phone_main} onChange={update('phone_main')} inputMode="tel" />
              </Field>
              <Field label="Email" wide>
                <div className="email-with-domain">
                  <input
                    type="text"
                    value={form.email_local}
                    onChange={update('email_local')}
                    placeholder="username"
                    autoComplete="off"
                  />
                  <span className="email-domain-suffix cell-mono">
                    {domain ? `@${domain}` : <span className="cell-muted">(no domain inherited)</span>}
                  </span>
                </div>
              </Field>
            </div>
          </fieldset>
        </>
      )}

      <div className="form-actions">
        <span className="form-hint">
          {selectedChain
            ? <>Fields marked <span className="req">*</span> are required.</>
            : 'Select a chain to continue.'}
        </span>
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
        <button type="submit" className="btn-primary" disabled={saving || !selectedChain}>
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, wide, required }) {
  return (
    <label className={`form-field${wide ? ' form-field-wide' : ''}`}>
      <span>
        {label}
        {required && <span className="req"> *</span>}
      </span>
      {children}
    </label>
  );
}
